import {
  Component,
  effect,
  ElementRef,
  forwardRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  CalendarValue,
  DatePickerDirective,
  DpDatePickerModule,
  ECalendarValue,
  IDatePickerDirectiveConfig,
  ISelectionEvent,
} from 'ng2-date-picker';
import { FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import de from 'dayjs/locale/de';
import dayjs from 'dayjs';
import { AbstractFormControlComponent } from '../abstract-form-control.component';

dayjs.locale(de);

export type DpCalendarValue = CalendarValue;
export type DpISelectionEvent = ISelectionEvent;
export type DpIDatePickerDirectiveConfig = IDatePickerDirectiveConfig;

export type TDrops = 'up' | 'down';
export type WeekDays = 'su' | 'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa';

@Component({
  selector: 'shared-ui-datepicker',
  standalone: true,
  imports: [NgClass, DpDatePickerModule, FormsModule, ReactiveFormsModule],
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true,
    },
  ],
})
export class DatepickerComponent extends AbstractFormControlComponent<string> {
  readonly name = input.required<string>();
  readonly label = input<string>('');
  readonly returnValueType = input<ECalendarValue>(ECalendarValue.String);
  readonly placeholderText = input<string>('Datum auswählen');
  readonly drops = input<TDrops | undefined>(undefined);
  readonly format = input<string | undefined>('DD.MM.YYYY');

  readonly additionalCloseCondition = input<boolean>(false);
  readonly additionalOpenCondition = input<boolean>(false);

  readonly errorText = input('Fehlerhafte Eingabe');
  readonly isInvalid = input<boolean>(false);

  closeEvent = output();
  focusEvent = output<FocusEvent>();
  clicked = output();

  readonly dateDirectivePicker = viewChild<DatePickerDirective>('datePickerDirective');
  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  readonly datePickerConfig: IDatePickerDirectiveConfig = {
    format: this.format(),
    drops: this.drops(),
    returnedValueType: this.returnValueType(),
    showGoToCurrent: false,
    firstDayOfWeek: 'mo' as WeekDays,
    showMultipleYearsNavigation: true,
    inputElementContainer: this.inputElement(),
  };

  constructor() {
    super();
    effect(() => {
      if (!this.additionalOpenCondition()) return;

      this.dateDirectivePicker()?.api?.open();
    });

    effect(() => {
      if (!this.additionalCloseCondition()) return;

      this.dateDirectivePicker()?.api?.close();
    });
  }

  /**
   * overrides default behavior to parse valid date without dots into date with dots if possible
   * */
  override onInternalControlValueChange(value: string | null) {
    if (!this.isValidDateWithoutDots(value)) {
      this.onChange(value);
      this.onTouched();

      return;
    }

    const parsedDate = dayjs(value, 'DDMMYYYY', true).format('DD.MM.YYYY');

    this.onChange(parsedDate);
    this.onTouched();
    this.internalControl.setValue(parsedDate, { emitEvent: false });
  }

  readonly isOpened = signal(false);

  onDatePickerOpen() {
    this.dateDirectivePicker()?.api?.moveCalendarTo(
      this.internalControl.value ? this.internalControl.value : dayjs(),
    );
  }

  private isValidDateWithoutDots(value: string | null): boolean {
    const date = dayjs(value, 'DDMMYYYY', true);

    return date.isValid();
  }
}
